/**
 * LegalDocument → normalized review outline.
 *
 * Extracted from `DocumentOutlinePanel`, which used to build this tree inline
 * as JSX. Emitting data instead lets one outline component render both the
 * JSON model and the fixed-DOCX model.
 *
 * The node ids and `defaultOpen` thresholds here are load-bearing: `id` is the
 * collapse-state key persisted by the review shell, so changing one silently
 * resets which sections a user had expanded. They intentionally match the
 * previous JSX one-for-one.
 */

import type { DocumentBlock, LegalDocument } from '@/schemas/schema';
import type {
  ReviewBlock,
  ReviewBlockKind,
  ReviewOutlineNode,
} from '@/modules/templates/components/review/adapter/types';
import { buildDocumentTextIndex, type TextBlockMapping } from './text-block-mapper';
import { buildVirtualBlocks } from './virtual-blocks';

type Translate = (key: string, values?: Record<string, string | number>) => string;

/** Number of top-level article/section groups expanded by default. */
const ROOT_OPEN_COUNT = 5;
/** Number of nested container groups expanded by default. */
const NESTED_OPEN_COUNT = 3;

export function toReviewBlock(mapping: TextBlockMapping): ReviewBlock {
  return {
    id: mapping.blockId,
    text: mapping.text,
    label: mapping.label,
    kind: mapping.type as ReviewBlockKind,
    isTitle: mapping.isTitle,
    source: mapping,
  };
}

/**
 * Recover the underlying mapping from a block produced by `toReviewBlock`.
 * Only valid for blocks this module created — the shell round-trips `source`
 * untouched, so the cast holds.
 */
export function fromReviewBlock(block: ReviewBlock): TextBlockMapping {
  return block.source as TextBlockMapping;
}

const leafNode = (mapping: TextBlockMapping): ReviewOutlineNode => ({
  id: mapping.blockId,
  kind: 'block',
  title: mapping.text,
  block: toReviewBlock(mapping),
});

interface OutlineTreeNode {
  block: DocumentBlock;
  path: number[];
  /** Every editable mapping at or below this node. */
  mappings: TextBlockMapping[];
  children: OutlineTreeNode[];
}

/** Build a tree of articles > sections > leaf blocks for the outline. */
function buildOutlineTree(
  blocks: DocumentBlock[],
  textIndex: TextBlockMapping[],
  path: number[] = []
): OutlineTreeNode[] {
  return blocks.map((block, idx) => {
    const currentPath = [...path, idx];

    const mappings = textIndex.filter((m) => {
      if (m.blockPath.length < currentPath.length) return false;
      return currentPath.every((p, i) => m.blockPath[i] === p);
    });

    const children = block.children ? buildOutlineTree(block.children, textIndex, currentPath) : [];

    return { block, path: currentPath, mappings, children };
  });
}

const isContainer = (node: OutlineTreeNode) =>
  node.block.type === 'article' ||
  node.block.type === 'section' ||
  !!(node.block.children && node.block.children.length > 0);

/**
 * Convert one article/section node. Children are emitted in the same order the
 * previous JSX used: the node's own direct mappings, then nested containers,
 * then the mappings of purely-leaf children.
 */
function articleNode(
  node: OutlineTreeNode,
  textIndex: TextBlockMapping[],
  defaultOpen: boolean
): ReviewOutlineNode {
  // Mappings that sit exactly at this level — in practice the node's own title.
  const directMappings = textIndex.filter(
    (m) =>
      m.blockPath.length === node.path.length && node.path.every((p, i) => m.blockPath[i] === p)
  );

  const containerChildren = node.children.filter(isContainer);
  const leafChildren = node.children.filter(
    (c) => !containerChildren.includes(c) && c.mappings.length > 0
  );

  const children: ReviewOutlineNode[] = [
    ...directMappings.map(leafNode),
    ...containerChildren.map((child, i) => articleNode(child, textIndex, i < NESTED_OPEN_COUNT)),
    ...leafChildren.flatMap((child) =>
      child.mappings
        .filter(
          (m) =>
            m.blockPath.length > node.path.length &&
            !directMappings.some((dm) => dm.blockId === m.blockId)
        )
        .map(leafNode)
    ),
  ];

  return {
    id: `section:${node.path.join('-')}`,
    kind: 'group',
    variant: 'section',
    title: node.block.props?.title || `${node.block.type}`,
    defaultOpen,
    children,
    blockIds: node.mappings.map((m) => m.blockId),
  };
}

/**
 * Build the full outline for a `LegalDocument`: a "Document" group for
 * metadata, the content tree, and a "Signatures" group for the signature page.
 *
 * Returns an empty array when the document has no editable content at all, so
 * callers can render their own empty state.
 */
export function buildLegalDocumentOutline(
  document: LegalDocument | null | undefined,
  t: Translate
): ReviewOutlineNode[] {
  // The outline is rebuilt on every render of the review page, which can happen
  // before the document has loaded. Nothing to outline is not an error.
  if (!document) return [];

  const textIndex = buildDocumentTextIndex(document);
  const virtualBlocks = buildVirtualBlocks(document, t);
  const tree = buildOutlineTree(document.content ?? [], textIndex);

  const nodes: ReviewOutlineNode[] = [];

  // Document-level fields (currently just the title).
  if (virtualBlocks.document.length > 0) {
    nodes.push({
      id: 'vgroup:document',
      kind: 'group',
      variant: 'virtual',
      title: t('GENERATEREVIEW_OUTLINE_DOCUMENTGROUP', { defaultValue: 'Document' }),
      icon: 'file',
      defaultOpen: true,
      children: virtualBlocks.document.map(leafNode),
      blockIds: virtualBlocks.document.map((m) => m.blockId),
    });
  }

  // The content tree. Top-level articles/sections become groups; anything else
  // contributes its mappings as root-level leaves.
  tree.forEach((node, i) => {
    if (node.block.type === 'article' || node.block.type === 'section') {
      nodes.push(articleNode(node, textIndex, i < ROOT_OPEN_COUNT));
    } else {
      nodes.push(...node.mappings.map(leafNode));
    }
  });

  // Signature-page content (heading, attestation, per-signatory fields).
  const hasSignatureBlocks =
    virtualBlocks.signatureGeneral.length > 0 || virtualBlocks.signatories.length > 0;

  if (hasSignatureBlocks) {
    const signerGroups: ReviewOutlineNode[] = virtualBlocks.signatories.map((signer, idx) => ({
      id: `vgroup:signer-${idx}`,
      kind: 'group',
      variant: 'virtual',
      title: signer.label,
      icon: 'signature',
      defaultOpen: false,
      children: signer.items.map(leafNode),
      blockIds: signer.items.map((m) => m.blockId),
    }));

    nodes.push({
      id: 'vgroup:signatures',
      kind: 'group',
      variant: 'virtual',
      title: t('GENERATEREVIEW_OUTLINE_SIGNATURESGROUP', { defaultValue: 'Signatures' }),
      icon: 'signature',
      defaultOpen: false,
      children: [...virtualBlocks.signatureGeneral.map(leafNode), ...signerGroups],
      blockIds: [
        ...virtualBlocks.signatureGeneral.map((m) => m.blockId),
        ...virtualBlocks.signatories.flatMap((s) => s.items.map((m) => m.blockId)),
      ],
    });
  }

  return nodes;
}
